import React, { useEffect, useRef, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useLoader } from "../../contexts/LoaderContext";
import {
  handleAdminBroadcastList,
  handleAdminDeleteBroadcast,
  handleAdminSendBroadcast,
  handleAdminUserList,
} from "../../services/APIClient";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { STATUS_CODES } from "../../utils/statusCode";
import Toster from "../../utils/Toaster";
import { removeUserData } from "../../store/slices/userSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import DeleteModel from "./DeleteModel";
import { TimeFormatter, debounce } from "../../utils/CommonFunction";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const BroadcastNotification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreBroadcasts, setHasMoreBroadcasts] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllBroadcast, setSelectAllBroadcast] = useState(false);
  const [selectBroadcast, setSelectBroadcast] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);
  const [activeTab, setActiveTab] = useState("sendBroadcast");
  const [broadcastList, setBroadcastList] = useState([]);
  const [broadcastPage, setBroadcastPage] = useState(1);
  const broadcastContainerRef = useRef(null);
  const [isLoadMoreTrue, setIsLoadMoreTrue] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sId, setID] = useState();
  const { loading, setLoading } = useLoader();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Rich text editor state
  const [messageContent, setMessageContent] = useState("");
  const [messageError, setMessageError] = useState("");

  // Simple mounted state for React apps
  const [isMounted, setIsMounted] = useState(false);

  // Ensure component is fully mounted before showing ReactQuill
  useEffect(() => {
    // Small delay ensures everything is loaded properly
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // Quill configuration
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
  ];

  const fetchUserList = async (currentPage, query, isLoadMore = 0) => {
    try {
      setLoading(true);
      const response = await handleAdminUserList({
        pageNumber: currentPage,
        search: query,
        listType: "broadCastUsers",
      });

      if (response?.code === STATUS_CODES.SUCCESS) {
        setIsLoadMoreTrue(response?.data?.load_more);
        const newUsers = response.data.UserList || [];

        const updatedUsers = newUsers.map((user) => ({
          ...user,
          checked: selectAll,
        }));

        if (isLoadMoreTrue) {
          if (isLoadMore === 1) {
            setUserList((prevUsers) => [...prevUsers, ...updatedUsers]);
          } else {
            setUserList([...updatedUsers]);
          }
        }

        setHasMore(newUsers.length > 0);
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to fetch content", "error");
      }
    } catch (error) {
      Toster("An error occurred while fetching content", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchBroadcastList = async (currentPage, isLoadMore = 0) => {
    try {
      setLoading(true);
      const response = await handleAdminBroadcastList({ pageNo: currentPage });

      if (response?.code === STATUS_CODES.SUCCESS) {
        const newBroadcasts = response.data.broadcastList || [];
        const updatedBroadcast = newBroadcasts.map((broadcast) => ({
          ...broadcast,
          checked: selectAllBroadcast,
        }));

        if (isLoadMore === 1) {
          setBroadcastList((prevUsers) => [...prevUsers, ...updatedBroadcast]);
        } else {
          setBroadcastList([...updatedBroadcast]);
        }

        setHasMoreBroadcasts(response?.data?.loadMore);
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to fetch broadcasts", "error");
      }
    } catch (error) {
      Toster("An error occurred while fetching broadcasts", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchUserList(page, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchBroadcastList(broadcastPage);
  }, []);

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight - 10 &&
        !loading &&
        hasMore
      ) {
        setPage((prevPage) => {
          const nextPage = prevPage + 1;
          fetchUserList(nextPage, searchQuery, 1);
          return nextPage;
        });
      }
    }
  };

  const handleBroadcastScroll = () => {
    if (broadcastContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        broadcastContainerRef.current;

      if (
        scrollTop + clientHeight >= scrollHeight - 10 &&
        !loading &&
        hasMoreBroadcasts
      ) {
        setBroadcastPage((prevPage) => {
          const nextPage = prevPage + 1;
          fetchBroadcastList(nextPage, 1);
          return nextPage;
        });
      }
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await handleAdminDeleteBroadcast({ id: sId });

      if (response?.code === STATUS_CODES.SUCCESS) {
        Toster("Broadcast deleted successfully", "success");
        setIsModalOpen(false);
        fetchBroadcastList(1, 0);
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to delete broadcast", "error");
      }
    } catch (error) {
      Toster("An error occurred while deleting broadcast", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    const allBroadcastSelected = selectAllBroadcast;
    const selectedBroadcastIds = broadcastList
      .filter((Broadcast) => Broadcast.checked)
      .map((Broadcast) => Broadcast._id)
      .join(",");

    if (!allBroadcastSelected && !selectedBroadcastIds) {
      Toster("Please select at least one broadcast", "error");
      setLoading(false);
      return;
    }
    setID(selectedBroadcastIds);
    setIsModalOpen(true);
  };

  const toggleSelectAll = () => {
    setSelectAll(!selectAll);
    setUserList((prevUsers) =>
      prevUsers.map((user) => ({
        ...user,
        checked: !selectAll,
      })),
    );
  };

  const toggleBroadcastSelectAll = () => {
    setSelectAllBroadcast(!selectAllBroadcast);
    setBroadcastList((prevBroadcast) =>
      prevBroadcast.map((broadcast) => ({
        ...broadcast,
        checked: !selectAllBroadcast,
      })),
    );
  };

  const toggleUserSelection = (index) => {
    setUserList((prevUsers) => {
      const updatedUsers = prevUsers.map((user, i) =>
        i === index ? { ...user, checked: !user.checked } : user,
      );
      const allChecked = updatedUsers.every((user) => user.checked);
      setSelectAll(allChecked);
      return updatedUsers;
    });
  };

  const toggleBroadcastSelection = (index) => {
    setBroadcastList((prevBroadcast) => {
      const updatedBroadcast = prevBroadcast.map((broadcast, i) =>
        i === index ? { ...broadcast, checked: !broadcast.checked } : broadcast,
      );
      const allChecked = updatedBroadcast.every(
        (broadcast) => broadcast.checked,
      );
      setSelectAllBroadcast(allChecked);
      return updatedBroadcast;
    });
  };

  const debouncedSetQuery = useCallback(
    debounce((value) => {
      setDebouncedSearchQuery(value);
    }, 400),
    [],
  );

  const handleSearchChange = (e) => {
    const trimmedValue = e.target.value.replace(/^\s+/, "");
    setSearchQuery(trimmedValue);
    setPage(1);
    setIsLoadMoreTrue(true);
    debouncedSetQuery(trimmedValue);
  };

  const handleQuillChange = (content, delta, source, editor) => {
    setMessageContent(content);
    const plainText = editor.getText().trim();

    if (plainText.length === 0) {
      setMessageError("Message is required");
    } else if (plainText.length < 20) {
      setMessageError("Message must be at least 20 characters");
    } else if (plainText.length > 1000) {
      setMessageError("Message cannot exceed 1000 characters");
    } else {
      setMessageError("");
    }
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    if (messageError) return;

    try {
      setLoading(true);
      let selectedUserIds;

      if (selectAll && !searchQuery) {
        const allUsersResponse = await handleAdminUserList({
          pageNumber: 1,
          search: "",
        });
        let allUsers = allUsersResponse?.data?.UserList || [];

        let currentPage = 1;
        while (allUsersResponse?.data?.load_more) {
          currentPage += 1;
          const nextPageResponse = await handleAdminUserList({
            pageNumber: currentPage,
            search: "",
          });
          const nextUsers = nextPageResponse?.data?.UserList || [];
          allUsers = [...allUsers, ...nextUsers];
          if (!nextPageResponse?.data?.load_more) break;
        }

        selectedUserIds = allUsers.map((user) => user._id).join(",");
      } else {
        const selected = userList.filter((user) => user.checked);
        if (selected.length === 0) {
          Toster("Please select at least one user", "error");
          setLoading(false);
          return;
        }
        selectedUserIds = selected.map((user) => user._id).join(",");
      }

      const sendData = {
        title: data.subject,
        description: messageContent,
        userId: selectedUserIds,
      };

      const response = await handleAdminSendBroadcast(sendData);

      if (response?.code === STATUS_CODES.SUCCESS) {
        Toster("Notification sent successfully", "success");
        setValue("subject", "");
        setMessageContent("");
        setMessageError("");
        setSelectAll(false);
        setActiveTab("broadcastList");
        fetchBroadcastList(1);

        // reset search state fully
        setSearchQuery("");
        setDebouncedSearchQuery("");

        // reset pagination state
        setPage(1);
        setHasMore(true);
        setIsLoadMoreTrue(true);

        // refetch with explicit empty string, not the stale `searchQuery` var
        fetchUserList(1, "");
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to send notification", "error");
      }
    } catch (error) {
      console.error(error);
      Toster("An error occurred while sending notification", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (index) => {
    setBroadcastList((prev) =>
      prev.map((b, i) => (i === index ? { ...b, showFull: !b.showFull } : b)),
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-7xl mx-auto mt-5">
      <div className="header-block flex justify-evenly items-center border-b pb-4 mb-6">
        <div>
          <button
            className={`text-gray-500 font-medium focus:outline-none ${
              activeTab === "sendBroadcast"
                ? "text-blue-500 border-b-2 border-blue-500"
                : "hover:text-blue-400"
            }`}
            onClick={() => setActiveTab("sendBroadcast")}
          >
            Send Broadcast
          </button>
        </div>
        <div className="border-l h-6"></div>
        <div>
          <button
            className={`text-gray-500 font-medium focus:outline-none ${
              activeTab === "broadcastList"
                ? "text-blue-500 border-b-2 border-blue-500"
                : "hover:text-blue-400"
            }`}
            onClick={() => setActiveTab("broadcastList")}
          >
            Broadcast List
          </button>
        </div>
      </div>

      {activeTab === "sendBroadcast" && (
        <div className="flex flex-col md:flex-row gap-8">
          {/* Left Panel - User List */}
          <div className="w-full md:w-1/2">
            <div className="relative mb-4">
              <img
                src="/search.png"
                alt="Search Icon"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full px-12 py-2 border text-black rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Search by name or email"
              />
            </div>

            <div>
              {userList.length > 0 && (
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                    className="mr-2"
                  />
                  <span className="font-medium">Select All</span>
                </div>
              )}
              <div
                ref={containerRef}
                className="space-y-4 overflow-auto"
                style={{ height: "400px" }}
                onScroll={(e) => {
                  const element = e.target;
                  if (
                    Math.abs(
                      element.scrollHeight -
                        element.scrollTop -
                        element.clientHeight,
                    ) <= 1 &&
                    userList.length > 0 &&
                    hasMore === true
                  ) {
                    handleScroll();
                  }
                }}
              >
                {userList.map((user, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 border-b pb-4 last:border-b-0 mr-4"
                  >
                    <img
                      src={user.profileImage}
                      alt={user.fullname}
                      className="w-10 h-10 rounded-full"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-700">
                        {user.fullname}
                      </p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={user.checked}
                      value={user._id}
                      onChange={() => toggleUserSelection(index)}
                    />
                  </div>
                ))}
                {loading && (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Broadcast Form */}
          <div className="w-full md:w-1/2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block font-medium mb-2">Subject</label>
                <input
                  {...register("subject", {
                    required: "Subject is required",
                    minLength: {
                      value: 8,
                      message: "Subject must be at least 8 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Subject cannot exceed 100 characters",
                    },
                    validate: (value) =>
                      !value.startsWith(" ") ||
                      "Subject should not start with space",
                  })}
                  type="text"
                  className="w-full px-4 py-2 border text-black rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Enter subject"
                  onInput={(e) => {
                    if (e.target.value.startsWith(" ")) {
                      e.target.value = e.target.value.trimStart();
                    }
                  }}
                />
                {errors.subject && (
                  <p className="text-red-500 text-sm">
                    {errors.subject.message}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="block font-medium mb-2">Message</label>
                <div className="react-quill-wrapper text-black">
                  {isMounted ? (
                    <ReactQuill
                      value={messageContent}
                      onChange={handleQuillChange}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter your message here..."
                      theme="snow"
                    />
                  ) : (
                    <div className="h-32 border rounded-md bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <span className="text-gray-500 text-sm">
                          Loading editor...
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {messageError && (
                  <p className="text-red-500 text-sm mt-2">{messageError}</p>
                )}
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={loading || messageError}
                  className={`px-4 py-2 w-full ${
                    loading || messageError
                      ? "bg-gray-500 cursor-not-allowed"
                      : "bg-gray-500"
                  } hover:bg-gray-600 text-white font-semibold rounded-md`}
                >
                  {loading ? "Sending..." : "Send Notification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === "broadcastList" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            {broadcastList.some((broadcast) => broadcast.checked) && (
              <button
                onClick={handleDeleteSelected}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Delete Selected
              </button>
            )}
          </div>
          <div
            ref={broadcastContainerRef}
            className="overflow-auto"
            style={{ height: "440px" }}
            onScroll={(e) => {
              const element = e.target;
              if (
                Math.abs(
                  element.scrollHeight -
                    element.scrollTop -
                    element.clientHeight,
                ) <= 1 &&
                broadcastList.length > 0 &&
                hasMoreBroadcasts === true &&
                !loading
              ) {
                handleBroadcastScroll();
              }
            }}
          >
            <table className="table-auto w-full border-collapse border border-gray-200">
              <thead className="sticky top-0 bg-[#252525] text-white z-1">
                <tr>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={selectAllBroadcast}
                      onChange={toggleBroadcastSelectAll}
                      className="mr-2"
                    />
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Title
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Descriptions
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Date
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {broadcastList.map((broadcast, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}
                  >
                    <td className="border border-gray-300 px-4 py-2">
                      <input
                        type="checkbox"
                        checked={broadcast.checked}
                        value={broadcast._id}
                        onChange={() => toggleBroadcastSelection(index)}
                      />
                    </td>

                    {/* Title */}
                    <td className="border border-gray-300 px-4 py-2 text-black">
                      {broadcast.subject}
                    </td>

                    {/* Description with expand/shrink */}
                    <td className="border border-gray-300 px-4 py-2 align-top text-black">
                      {isMounted && (
                        <div className="w-full">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: broadcast.message,
                            }}
                            className={
                              broadcast.showFull
                                ? "expanded-text"
                                : "clamped-text"
                            }
                          />
                          {broadcast.message.length > 200 && (
                            <div className="flex justify-end mt-2">
                              <button
                                onClick={() => toggleExpand(index)}
                                className="text-white hover:bg-blue-600 text-sm font-medium rounded px-2 py-1 transition bg-blue-400"
                              >
                                {broadcast.showFull ? "Read Less" : "Read More"}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Date */}
                    <td className="border border-gray-300 px-4 py-2 text-black">
                      {TimeFormatter(broadcast.createdAt)}
                    </td>

                    {/* Action */}
                    <td className="border border-gray-300 px-4 py-2">
                      <button
                        onClick={() => {
                          setID(broadcast._id);
                          setIsModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700"
                        data-id={broadcast._id}
                      >
                        <FontAwesomeIcon icon={faTrash} className="h-5 w-5" />
                      </button>
                      <DeleteModel
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onConfirm={() => handleDelete()}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loading}
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastNotification;
